import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/apiBase';

const API_URL = getApiBaseUrl();

const STORAGE_KEY = 'app_language';

const TranslationContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  tFormat: (key) => key,
  languages: [],
  loading: true,
  ready: false,
  coveragePercent: null,
});

export function TranslationProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'en';
    } catch {
      return 'en';
    }
  });
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coveragePercent, setCoveragePercent] = useState(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) != null) return;
    } catch {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        const def = res.data?.default_language;
        if (!cancelled && def && typeof def === 'string' && def) {
          setLanguageState(def);
          try {
            localStorage.setItem(STORAGE_KEY, def);
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setLanguage = useCallback((code) => {
    setLanguageState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
    try {
      document.documentElement.lang = code.split('-')[0] || 'en';
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [langRes, trRes] = await Promise.all([
          axios.get(`${API_URL}/translations/languages`),
          axios.get(`${API_URL}/translations/${encodeURIComponent(language)}`),
        ]);
        if (cancelled) return;
        setLanguages(Array.isArray(langRes.data) ? langRes.data : []);
        setTranslations(
          trRes.data?.translations && typeof trRes.data.translations === 'object'
            ? trRes.data.translations
            : {}
        );
        const cp = trRes.data?.coverage_percent;
        setCoveragePercent(typeof cp === 'number' ? cp : null);
      } catch (e) {
        console.error('Failed to load translations', e);
        if (!cancelled) {
          setLanguages([]);
          setTranslations({});
          setCoveragePercent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [language]);

  const t = useCallback(
    (key, fallback = '') => {
      if (translations[key] != null && String(translations[key]).length > 0) {
        return String(translations[key]);
      }
      return fallback || key;
    },
    [translations]
  );

  const tFormat = useCallback(
    (key, params, fallback = '') => {
      let s = t(key, fallback);
      if (params && typeof params === 'object') {
        Object.entries(params).forEach(([k, v]) => {
          s = s.split(`{${k}}`).join(String(v));
        });
      }
      return s;
    },
    [t]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      tFormat,
      languages,
      loading,
      ready: !loading,
      coveragePercent,
    }),
    [language, setLanguage, t, tFormat, languages, loading, coveragePercent]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}
